import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import LandingLoader from "@/components/landing-v2/LandingLoader";
import HeroScene from "@/components/landing-v2/HeroScene";
import ROICalculator from "@/components/landing-v2/ROICalculator";

/**
 * Landing v2 — Luni/Omada-inspired pastel diorama.
 *
 *   1. Boot loader (counts $0 → $2,847,302 settled, ~3.2s, then fades out)
 *   2. Hero: pastel sky + 3D creator-money diorama (invoice, coins, bill, chips)
 *   3. "Discover Quill" CTA pill
 *   4. Pricing pillows
 *   5. Footer
 *
 * Mobile-first: clamp() typography, smaller perspective, reduced chip count,
 * pill CTAs that meet 48px tap target.
 */

const FEATURES = [
  {
    title: "Invoice in minutes",
    body: "Wax-sealed, branded PDFs your clients actually like opening.",
    bg: "var(--l2-mint)",
  },
  {
    title: "Get paid in pure crypto",
    body: "ETH and BTC, settled straight to your non-custodial wallet. No cards. No middlemen. No chargebacks.",
    bg: "var(--l2-peach)",
  },
  {
    title: "Tax-ready at year-end",
    body: "Every on-chain payment lands in your ledger with a cost basis. Export a filled 8949 in one click.",
    bg: "var(--l2-pink)",
  },
];

const TIERS = [
  {
    name: "Free",
    price: "$0",
    sub: "per month",
    perks: ["3 invoices / month", "Wallet + payouts", "Drafted with Quill"],
    accent: "var(--l2-cream-2)",
    cta: "Start free",
  },
  {
    name: "Pro",
    price: "$12",
    sub: "per month",
    perks: [
      "Pay in ETH or BTC — straight to your wallet",
      "Unlimited invoices · No watermark",
      "Custom branding + Tax ID",
      "AI Invoice Assistant",
    ],
    accent: "var(--l2-mint)",
    cta: "Go Pro",
    featured: true,
  },
  {
    name: "Team",
    price: "$29",
    sub: "per month",
    perks: [
      "Everything in Pro",
      "Pay in ETH or BTC — settle in seconds",
      "Auto-splits to teammates",
      "Priority support",
    ],
    accent: "var(--l2-peach)",
    cta: "Start Team",
  },
  {
    name: "Studio",
    price: "$99",
    sub: "per month",
    perks: [
      "Everything in Team",
      "White-glove tax filing — we file your 8949",
      "API access + webhooks",
      "Private Slack support channel",
    ],
    accent: "var(--l2-pink)",
    cta: "Go Studio",
    badge: "For $100K+ freelancers",
  },
];

const Landing = () => {
  const { login, user } = useAuth();
  const [bootDone, setBootDone] = useState(false);

  // Show loader only on first hit per session
  useEffect(() => {
    try {
      if (sessionStorage.getItem("quill-boot-done") === "1") {
        setBootDone(true);
      }
    } catch (_) { /* private mode — show loader anyway */ }
  }, []);

  // Capture any `?ref=CODE` for referral attribution on next signup.
  // Persists 30 days in localStorage so a creator can share the link,
  // the visitor can browse, and get attributed even after auth redirect.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get("ref") || "").trim().toUpperCase();
      if (ref && /^[A-Z0-9]{4,16}$/.test(ref)) {
        window.localStorage.setItem("quill_ref_code", ref);
        // Best-effort click ping — never blocks the page.
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/track-click`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: ref }),
        }).catch(() => {});
      }
    } catch (_) { /* ignore */ }
  }, []);

  const onBootDone = () => {
    setBootDone(true);
    try { sessionStorage.setItem("quill-boot-done", "1"); } catch (_) { /* noop */ }
  };

  const onCTA = () => (user ? (window.location.href = "/dashboard") : login());

  return (
    <>
      {!bootDone && <LandingLoader onDone={onBootDone} />}

      <div className="landing-v2 min-h-screen" data-testid="landing-v2">
        {/* ── Nav */}
        <header className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-5 md:py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center l2-display text-base"
              style={{ background: "var(--l2-ink)", color: "var(--l2-cream)" }}
            >
              Q
            </div>
            <span className="l2-display text-base md:text-lg">Quill</span>
          </div>
          <nav className="hidden md:flex items-center gap-7 l2-mono text-[13px]">
            <a href="#features" className="hover:opacity-70 transition-opacity">Features</a>
            <a href="#pricing"  className="hover:opacity-70 transition-opacity">Pricing</a>
            <a href="/tax-pack" className="hover:opacity-70 transition-opacity">Tax Pack</a>
          </nav>
          <div className="flex items-center gap-3">
            {!user && (
              <Link
                to="/login"
                data-testid="nav-email-signin-link"
                className="hidden md:inline-flex l2-mono text-[12px] text-[color:var(--l2-ink-2)] hover:text-[color:var(--l2-ink)] transition-colors"
              >
                Email sign-in
              </Link>
            )}
            <button
              onClick={onCTA}
              data-testid="nav-cta-btn"
              className="l2-cta l2-pill l2-pill-dark cursor-pointer transition-transform hover:-translate-y-0.5"
            >
              {user ? "Dashboard" : "Sign in with Google"}
            </button>
          </div>
        </header>

        {/* ── Hero */}
        <section className="relative max-w-7xl mx-auto px-5 md:px-10 pt-4 md:pt-8 pb-16 md:pb-24">
          {/* Tagline — tiny, bottom-left of the diorama (Luni style) */}
          <div className="hidden md:block absolute left-10 bottom-12 max-w-xs z-20">
            <div className="l2-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--l2-ink-2)" }}>
              Quill — crypto invoicing for freelancers
            </div>
            <div className="l2-display text-[28px] leading-tight mt-2" style={{ color: "var(--l2-ink)" }}>
              Bill in ETH or BTC.<br />Settle to your wallet.
            </div>
          </div>

          {/* The diorama */}
          <HeroScene />

          {/* Mobile tagline (below scene) */}
          <div className="md:hidden mt-2 px-2">
            <div className="l2-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--l2-ink-2)" }}>
              Quill — crypto invoicing for freelancers
            </div>
            <div className="l2-display text-[28px] leading-tight mt-2" style={{ color: "var(--l2-ink)" }}>
              Bill in ETH or BTC.<br />Settle to your wallet.
            </div>
          </div>

          {/* Discover CTA — bottom-right pill, Luni style */}
          <div className="relative z-20 mt-8 md:mt-0 md:absolute md:right-10 md:bottom-12 flex items-center gap-3">
            <button
              onClick={onCTA}
              data-testid="hero-cta-btn"
              className="l2-cta l2-pill cursor-pointer transition-transform hover:-translate-y-0.5"
              style={{ paddingLeft: "1.25rem", paddingRight: "1.25rem" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
              <span className="ml-1">
                <span className="block l2-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--l2-ink-2)" }}>
                  Discover
                </span>
                <span className="block l2-display text-[15px] -mt-0.5">Quill</span>
              </span>
            </button>
            {!user && (
              <Link
                to="/signup"
                data-testid="hero-email-signup-link"
                className="hidden md:inline-flex items-center l2-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--l2-ink-2)] hover:text-[color:var(--l2-ink)] transition-colors"
              >
                or sign up with email →
              </Link>
            )}
          </div>
        </section>

        {/* ── Features */}
        <section
          id="features"
          className="relative max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-20"
        >
          <div className="l2-mono text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--l2-ink-2)" }}>
            Features
          </div>
          <h2
            className="l2-display"
            style={{
              fontSize: "clamp(36px, 6vw, 72px)",
              color: "var(--l2-ink)",
              maxWidth: "12ch",
            }}
          >
            A studio in your back pocket.
          </h2>

          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                data-testid={`feature-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 0.61, 0.36, 1] }}
                whileHover={{ y: -6, rotateZ: i % 2 === 0 ? -1 : 1 }}
                className="relative rounded-[24px] p-6 md:p-8 cursor-default"
                style={{
                  background: f.bg,
                  boxShadow: "0 20px 40px -16px rgba(31, 27, 23, 0.18)",
                  minHeight: "240px",
                }}
              >
                <div
                  className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center l2-display text-sm"
                  style={{ background: "var(--l2-ink)", color: "var(--l2-cream)" }}
                >
                  {i + 1}
                </div>
                <div className="h-12" />
                <div
                  className="l2-display"
                  style={{ fontSize: "clamp(22px, 2.4vw, 30px)", color: "var(--l2-ink)" }}
                >
                  {f.title}
                </div>
                <p
                  className="mt-3 text-[14px] md:text-[15px] leading-relaxed"
                  style={{ color: "var(--l2-ink-2)" }}
                >
                  {f.body}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── ROI Calculator — anchors $12/mo against $400–1,200/yr alt stack */}
        <ROICalculator onCTA={onCTA} />

        {/* ── Pricing */}
        <section
          id="pricing"
          className="relative max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-20"
        >
          <div className="l2-mono text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--l2-ink-2)" }}>
            Pricing
          </div>
          <h2
            className="l2-display"
            style={{ fontSize: "clamp(36px, 6vw, 72px)", color: "var(--l2-ink)" }}
          >
            Replace your $400–1,200<br />stack for $12.
          </h2>
          <p
            className="mt-4 max-w-xl text-[15px] leading-relaxed"
            style={{ color: "var(--l2-ink-2)" }}
          >
            CPA, invoicing SaaS, crypto-tax tool, payment rake — bundled.
            One subscription, settled to your wallet.
          </p>

          <div className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {TIERS.map((t, i) => (
              <motion.div
                key={t.name}
                data-testid={`pricing-card-${t.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
                whileHover={{ y: -8 }}
                className="relative rounded-[24px] p-6 md:p-7 flex flex-col"
                style={{
                  background: t.featured ? "var(--l2-ink)" : t.accent,
                  color: t.featured ? "var(--l2-cream)" : "var(--l2-ink)",
                  boxShadow: t.featured
                    ? "0 30px 60px -20px rgba(31, 27, 23, 0.45)"
                    : "0 20px 40px -16px rgba(31, 27, 23, 0.18)",
                  minHeight: "360px",
                  transform: t.featured ? "scale(1.02)" : "none",
                }}
              >
                {t.featured && (
                  <div
                    className="absolute -top-3 right-6 l2-pill"
                    style={{ background: "var(--l2-peach-2)", color: "var(--l2-ink)" }}
                  >
                    <span className="l2-mono text-[10px] uppercase tracking-wider">Most popular</span>
                  </div>
                )}
                {t.badge && !t.featured && (
                  <div
                    className="absolute -top-3 right-6 l2-pill"
                    style={{ background: "var(--l2-ink)", color: "var(--l2-cream)" }}
                  >
                    <span className="l2-mono text-[10px] uppercase tracking-wider">{t.badge}</span>
                  </div>
                )}
                <div className="l2-display" style={{ fontSize: "clamp(20px, 2vw, 24px)" }}>
                  {t.name}
                </div>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="l2-display" style={{ fontSize: "clamp(48px, 5.4vw, 64px)" }}>
                    {t.price}
                  </span>
                  <span className="l2-mono text-[12px] opacity-70">{t.sub}</span>
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-[14px]">
                      <span
                        className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: t.featured ? "var(--l2-cream)" : "var(--l2-ink)" }}
                      />
                      <span style={{ opacity: t.featured ? 0.9 : 0.75 }}>{p}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onCTA}
                  data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  className="l2-cta mt-6 w-full rounded-full py-3 cursor-pointer transition-transform hover:-translate-y-0.5 l2-display"
                  style={{
                    background: t.featured ? "var(--l2-cream)" : "var(--l2-ink)",
                    color: t.featured ? "var(--l2-ink)" : "var(--l2-cream)",
                    fontSize: "14px",
                  }}
                >
                  {t.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── Footer CTA */}
        <section className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div
            className="rounded-[28px] p-8 md:p-16 text-center relative overflow-hidden"
            style={{
              background:
                "radial-gradient(ellipse at top, var(--l2-peach) 0%, var(--l2-pink) 80%)",
              boxShadow: "0 40px 60px -30px rgba(31, 27, 23, 0.3)",
            }}
          >
            <div className="l2-mono text-[11px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--l2-ink-2)" }}>
              Start in 60 seconds
            </div>
            <h3
              className="l2-display"
              style={{ fontSize: "clamp(32px, 5.5vw, 64px)", color: "var(--l2-ink)" }}
            >
              Your first invoice<br />is on the house.
            </h3>
            <button
              onClick={onCTA}
              data-testid="footer-cta-btn"
              className="l2-cta l2-pill l2-pill-dark mt-8 cursor-pointer transition-transform hover:-translate-y-0.5"
              style={{ padding: "0.875rem 1.75rem", fontSize: "14px" }}
            >
              {user ? "Open dashboard" : "Continue with Google"}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-3 l2-mono text-[12px]" style={{ color: "var(--l2-ink-2)" }}>
            <div>© 2026 Quill · Non-custodial · No platform fee</div>
            <div className="flex items-center gap-5">
              <a href="/terms" className="hover:opacity-70">Terms</a>
              <a href="/privacy" className="hover:opacity-70">Privacy</a>
              <a href="mailto:hi@quillpayed.com" className="hover:opacity-70">Say hi</a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Landing;
